import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Container, Paper, TextField, Button, CircularProgress, Alert, Checkbox, FormControlLabel } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getAssinaturaPublica, getAssinaturaByHashCurto, solicitarCodigoOtp, confirmarAssinatura } from '../../services/api';

const AssinaturaPage: React.FC = () => {
    const { token, hash } = useParams<{ token?: string, hash?: string }>();
    const identifier = token || hash;
    const [loading, setLoading] = useState(true);
    const [assinaturaData, setAssinaturaData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState(1);
    
    // Form fields
    const [codigo, setCodigo] = useState('');
    const [nomeConfirmacao, setNomeConfirmacao] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [aceite, setAceite] = useState(false);

    useEffect(() => {
        const fetchAssinatura = async () => {
            if (!identifier) return;
            try {
                setLoading(true);
                const data = hash ? await getAssinaturaByHashCurto(hash) : await getAssinaturaPublica(token!);
                
                if (data.status !== 'PENDENTE') {
                    setError('Este link de assinatura já foi utilizado, expirado ou cancelado.');
                } else {
                    setAssinaturaData(data);
                    setNomeConfirmacao(data.nome_assinante); // Pre-fill with the name expected
                }
            } catch (err: any) {
                setError('Assinatura não encontrada ou link inválido.');
            } finally {
                setLoading(false);
            }
        };
        fetchAssinatura();
    }, [token, hash, identifier]);

    const handleRequestCode = async () => {
        if (!assinaturaData?.token_acesso) return;
        try {
            setSubmitting(true);
            setError(null);
            await solicitarCodigoOtp(assinaturaData.token_acesso);
            setStep(2);
        } catch (err: any) {
            const tipoMsg = assinaturaData?.metodo_envio === 'sms' ? 'seu SMS' : assinaturaData?.metodo_envio === 'ambos' ? 'seu e-mail ou SMS' : 'seu e-mail';
            setError(err.message || `Erro ao enviar código para ${tipoMsg}.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmSignature = async () => {
        if (!assinaturaData?.token_acesso || !codigo) {
            setError('Preencha o código de verificação.');
            return;
        }
        try {
            setSubmitting(true);
            setError(null);
            await confirmarAssinatura(assinaturaData.token_acesso, codigo, nomeConfirmacao);
            setStep(3);
        } catch (err: any) {
            setError(err.message || 'Código inválido ou erro ao confirmar assinatura.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="error" variant="filled">{error}</Alert>
            </Container>
        );
    }

    if (step === 3) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h5" gutterBottom>Assinatura Confirmada!</Typography>
                    <Typography variant="body1">O recibo foi assinado com sucesso e arquivado no sistema.</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
                        Você pode fechar esta janela.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    const lanc = assinaturaData?.lancamentos_financeiros;

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" align="center" gutterBottom>Assinatura Digital de Recibo</Typography>
                <Box sx={{ my: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary">Fornecedor / Beneficiário:</Typography>
                    <Typography variant="body1" gutterBottom>{lanc?.fornecedores?.nome_razao_social || assinaturaData?.nome_assinante}</Typography>
                    
                    <Typography variant="body2" color="textSecondary">Valor Líquido:</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="bold">
                        {lanc?.valor_total_liquido ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lanc.valor_total_liquido) : 'R$ 0,00'}
                    </Typography>
                </Box>

                {step === 1 ? (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="body1" gutterBottom>
                            Para assinar este recibo digitalmente, enviaremos um código de verificação de 6 dígitos para o seu contato:
                            <br/>
                            {(!assinaturaData?.metodo_envio || assinaturaData?.metodo_envio === 'email' || assinaturaData?.metodo_envio === 'ambos') && (
                                <strong>E-mail: {assinaturaData?.email_assinante} <br/></strong>
                            )}
                            {(assinaturaData?.metodo_envio === 'sms' || assinaturaData?.metodo_envio === 'ambos') && assinaturaData?.telefone_assinante && (
                                <strong>SMS: Final {assinaturaData.telefone_assinante.slice(-4)} <br/></strong>
                            )}
                        </Typography>

                        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1, textAlign: 'left' }}>
                            <FormControlLabel
                                control={<Checkbox checked={aceite} onChange={(e) => setAceite(e.target.checked)} color="primary" sx={{ mt: -0.5 }}/>}
                                label={<Typography variant="body2" color="textSecondary" sx={{ textAlign: 'justify' }}>"Concordo em utilizar o sistema interno da Contratante para aceite e assinatura eletrônica de recibos e aditivos, reconhecendo como válida a assinatura realizada mediante verificação por código enviado {(assinaturaData?.metodo_envio === 'sms') ? 'ao telefone cadastrado' : (assinaturaData?.metodo_envio === 'ambos') ? 'ao e-mail ou telefone cadastrados' : 'ao e-mail cadastrado'}, nos termos do Art. 10, § 2º, da Medida Provisória nº 2.200-2/2001."</Typography>}
                                sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                        </Box>

                        <Button 
                            variant="contained" 
                            color="primary" 
                            size="large" 
                            onClick={handleRequestCode}
                            disabled={submitting || !aceite}
                            sx={{ mt: 3 }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Enviar Código'}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ mt: 4 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            O código foi enviado. Verifique {assinaturaData?.metodo_envio === 'sms' ? 'seu SMS' : assinaturaData?.metodo_envio === 'ambos' ? 'seu e-mail ou SMS' : 'seu e-mail'}.
                        </Alert>
                        
                        <TextField
                            label="Código de Verificação"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={codigo}
                            onChange={(e) => setCodigo(e.target.value)}
                            placeholder="Ex: 123456"
                            inputProps={{ maxLength: 6 }}
                        />
                        


                        <Button 
                            variant="contained" 
                            color="success" 
                            size="large" 
                            fullWidth
                            onClick={handleConfirmSignature}
                            disabled={submitting || codigo.length < 6}
                            sx={{ mt: 3 }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Assinatura'}
                        </Button>
                        <Button 
                            variant="text" 
                            fullWidth
                            onClick={handleRequestCode}
                            disabled={submitting}
                            sx={{ mt: 1 }}
                        >
                            Reenviar Código
                        </Button>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default AssinaturaPage;
